/**
 * Property-based subscription context for Lehti AI.
 * Type contracts + context object + consumer hooks. The Provider component
 * (and its decomposed data hooks) live in PropertySubscriptionProvider.tsx so
 * this module stays free of react-refresh/only-export-components warnings.
 */

import { createContext, useContext } from "react";

export interface PricingTier {
  tier: "free" | "plus" | "pro";
  display_name: string;
  price_eur: number;
  price_usd?: number;
  marketing_description: string;
  max_simulations_per_year: number;
  max_properties: number;
  features: string[];
}

export interface PropertySubscription {
  id: string;
  property_id: string;
  property_name: string;
  municipality: string;
  area_hectares?: number;
  tier: "free" | "plus" | "pro";
  tier_display: string;
  annual_cost: number;
  started_at: string;
  expires_at?: string;
  is_expired: boolean;
  /** True while the subscription record is active; false for expired paid records.
   *  Expired paid subscriptions are still returned by the API so the property
   *  remains visible on the map, but is_active=false means premium features are
   *  no longer available. */
  is_active?: boolean;
  /** Two-SKU flat key: 'plot_package' | 'portfolio_estate' | 'offer_decode' | undefined (free). */
  sku_key?: string;
  /** Canonical server-owned Plot Package decision for this exact property. */
  package_state: "active" | "expired" | "locked";
  /** Server-owned: a current Plot Package inside the renewal window
   *  (forest_models PLOT_PACKAGE_EXPIRING_WINDOW_DAYS). Rendered, never re-derived. */
  package_expiring: boolean;
  simulations_used: number;
  can_run_simulation: boolean;
  auto_renew: boolean;
  last_simulation_date?: string;
  features?: Record<string, boolean>;
}

export interface SubscriptionSummary {
  total_properties: number;
  total_annual_cost: number;
  free_properties: number;
  paid_properties: number;
}

export interface PropertySubscriptionContextType {
  // Property subscriptions
  propertySubscriptions: PropertySubscription[];
  summary: SubscriptionSummary | null;

  // Pricing tiers
  pricingTiers: PricingTier[];

  // Loading states
  isLoading: boolean;
  // True after the first successful (or failed) subscription load for the current
  // auth session. Consumers should treat an empty subscription list as "still loading"
  // when isAuthenticated && !hasAttemptedLoad.
  hasAttemptedLoad: boolean;
  error: string | null;

  // Actions
  refreshSubscriptions: () => Promise<void>;
  loadPricingTiers: () => Promise<void>;
  addPropertyToList: (propertyData: {
    property_id: string;
    property_name?: string;
    municipality?: string;
    area_hectares?: number;
  }) => Promise<{
    success: boolean;
    error?: string;
    upgrade_required?: boolean;
  }>;
  cancelPropertySubscription: (
    propertyId: string,
    reason?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  recordSimulationUsage: (
    propertyId: string,
  ) => Promise<{ success: boolean; error?: string }>;

  // Utility functions
  getPropertySubscription: (propertyId: string) => PropertySubscription | null;
  canAccessFeature: (propertyId: string, feature: string) => boolean;
  canRunSimulation: (propertyId: string) => boolean;
  getAnnualCostForProperties: (propertyIds: string[]) => number;
}

export const PropertySubscriptionContext = createContext<
  PropertySubscriptionContextType | undefined
>(undefined);

// Hook to use property subscription context
export const usePropertySubscription = (): PropertySubscriptionContextType => {
  const context = useContext(PropertySubscriptionContext);
  if (!context) {
    throw new Error(
      "usePropertySubscription must be used within a PropertySubscriptionProvider",
    );
  }
  return context;
};

// Hook for specific property subscription
export const usePropertySubscriptionDetails = (propertyId: string) => {
  const { getPropertySubscription, canAccessFeature, canRunSimulation } =
    usePropertySubscription();

  const subscription = getPropertySubscription(propertyId);

  return {
    subscription,
    canAccessFeature: (feature: string) =>
      canAccessFeature(propertyId, feature),
    canRunSimulation: canRunSimulation(propertyId),
    isSubscribed: !!subscription,
    tier: subscription?.tier || null,
    annualCost: subscription?.annual_cost || 0,
  };
};

export default PropertySubscriptionContext;
